import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, ArrowRight, Facebook, Linkedin, Twitter, Link2, MessageCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { BlogCard } from "../components/BlogCard";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = typeof window !== "undefined" ? window.location.origin : "https://vetrodesignitalia.it";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setState("loading");
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => {
        if (r.data.redirect) {
          navigate(`/blog/${r.data.redirect}`, { replace: true });
          return;
        }
        setArticle(r.data.article);
        setState("ok");
        axios.get(`${API}/blog/${slug}/related`).then((rr) => setRelated(rr.data)).catch(() => {});
      })
      .catch(() => setState("notfound"));
  }, [slug, navigate]);

  if (state === "loading") {
    return (
      <div data-testid="blog-article-loading" className="min-h-screen flex items-center justify-center text-foreground/50">
        Caricamento...
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div data-testid="blog-article-notfound">
        <Seo title="Articolo non trovato | VETRO DESIGN ITALIA" description="Articolo non disponibile." path={`/blog/${slug}`} noindex />
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6 px-6 text-center pt-24">
          <h1 className="font-serif text-3xl">Articolo non trovato</h1>
          <Link to="/blog" className="text-sm uppercase tracking-wide border border-black px-6 py-3 hover:bg-black hover:text-white transition-colors">
            Torna al Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const url = `${SITE}/blog/${article.slug}`;
  const shareText = encodeURIComponent(article.title);
  const shareUrl = encodeURIComponent(url);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.meta_description,
        image: article.cover_image,
        datePublished: article.published_at,
        dateModified: article.updated_at || article.published_at,
        author: { "@type": "Organization", name: "VETRO DESIGN ITALIA" },
        publisher: { "@type": "Organization", name: "VETRO DESIGN ITALIA" },
        mainEntityOfPage: url,
        articleSection: article.category,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
          { "@type": "ListItem", position: 2, name: "Blog", item: SITE + "/blog" },
          { "@type": "ListItem", position: 3, name: article.category, item: `${SITE}/blog?categoria=${encodeURIComponent(article.category)}` },
          { "@type": "ListItem", position: 4, name: article.title, item: url },
        ],
      },
      ...(article.faqs?.length
        ? [{
            "@type": "FAQPage",
            mainEntity: article.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }]
        : []),
    ],
  };

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => toast.success("Link copiato"));
  };

  return (
    <div data-testid="blog-article-page">
      <Seo title={article.meta_title} description={article.meta_description} path={`/blog/${article.slug}`} jsonLd={jsonLd} image={article.cover_image} type="article" />
      <Navbar />

      <article className="pt-28 md:pt-36">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <nav aria-label="breadcrumb" className="text-xs text-foreground/50 mb-8" data-testid="article-breadcrumb">
            <Link to="/" className="link-underline">Home</Link> <span className="mx-2">/</span>
            <Link to="/blog" className="link-underline">Blog</Link> <span className="mx-2">/</span>
            <span className="text-champagne">{article.category}</span>
          </nav>

          <p className="text-xs uppercase tracking-[0.3em] text-champagne mb-5">{article.category}</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight" data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-5 mt-6 text-sm text-foreground/50">
            <span>{fmtDate(article.published_at)}</span>
            <span className="flex items-center gap-1.5">
              <Clock strokeWidth={1.2} className="w-4 h-4" /> {article.reading_time} min di lettura
            </span>
            {article.updated_at && (
              <span data-testid="article-updated">Ultimo aggiornamento: {fmtDate(article.updated_at)}</span>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-12 mt-10">
          {article.cover_image && (
            <div className="aspect-[16/9] overflow-hidden bg-muted">
              <img src={article.cover_image} alt={article.cover_alt || article.title} className="w-full h-full object-cover" />
            </div>
          )}
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-12 mt-12">
          <div className="article-body" data-testid="article-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
          </div>

          {article.faqs?.length > 0 && (
            <div className="mt-16" data-testid="article-faqs">
              <h2 className="font-serif font-light text-3xl tracking-tight mb-6">Domande frequenti</h2>
              <Accordion type="single" collapsible className="border-t border-black/10">
                {article.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b border-black/10">
                    <AccordionTrigger className="text-left font-light text-base py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-foreground/70 font-light leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          <div className="mt-12 flex items-center gap-3 flex-wrap" data-testid="article-share">
            <span className="text-xs uppercase tracking-wide text-foreground/50 mr-2">Condividi</span>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 flex items-center justify-center border border-black/15 hover:bg-black hover:text-white transition-colors">
              <Facebook strokeWidth={1.2} className="w-4 h-4" />
            </a>
            <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 flex items-center justify-center border border-black/15 hover:bg-black hover:text-white transition-colors">
              <MessageCircle strokeWidth={1.2} className="w-4 h-4" />
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 flex items-center justify-center border border-black/15 hover:bg-black hover:text-white transition-colors">
              <Linkedin strokeWidth={1.2} className="w-4 h-4" />
            </a>
            <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label="X" className="w-10 h-10 flex items-center justify-center border border-black/15 hover:bg-black hover:text-white transition-colors">
              <Twitter strokeWidth={1.2} className="w-4 h-4" />
            </a>
            <button onClick={copyLink} aria-label="Copia link" data-testid="article-copy-link" className="w-10 h-10 flex items-center justify-center border border-black/15 hover:bg-black hover:text-white transition-colors">
              <Link2 strokeWidth={1.2} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Final CTA */}
        <div className="max-w-3xl mx-auto px-6 md:px-12 mt-16">
          <div className="bg-black text-white p-10 md:p-14 text-center" data-testid="article-cta">
            <p className="text-xs uppercase tracking-[0.3em] text-champagne mb-4">VETRO DESIGN ITALIA</p>
            <h2 className="font-serif font-light text-3xl sm:text-4xl leading-tight tracking-tight mb-4">
              Richiedi un sopralluogo gratuito
            </h2>
            <p className="text-white/70 font-light leading-relaxed max-w-xl mx-auto mb-8">
              Contattaci per una consulenza senza impegno. Operiamo in tutta la regione Marche con soluzioni su
              misura per terrazzi, balconi e spazi esterni.
            </p>
            <Link
              to="/contatti-vetrate-panoramiche"
              data-testid="article-cta-button"
              onClick={() => window.scrollTo({ top: 0 })}
              className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 text-sm uppercase tracking-wide hover:bg-champagne hover:text-white transition-colors duration-300"
            >
              Richiedi un preventivo <ArrowRight strokeWidth={1.2} className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Potrebbe interessarti anche */}
        <div className="max-w-3xl mx-auto px-6 md:px-12 mt-16" data-testid="article-suggestions">
          <h2 className="font-serif font-light text-2xl tracking-tight mb-6">Potrebbe interessarti anche</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/vetrate-panoramiche-vepa" onClick={() => window.scrollTo({ top: 0 })} data-testid="suggestion-vepa" className="group border border-black/10 p-5 flex items-center justify-between hover:border-black transition-colors">
              <span className="font-light">VEPA</span>
              <ArrowRight strokeWidth={1.2} className="w-4 h-4 text-champagne group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to={`/blog?categoria=${encodeURIComponent("Pergole Bioclimatiche")}`} onClick={() => window.scrollTo({ top: 0 })} data-testid="suggestion-pergole" className="group border border-black/10 p-5 flex items-center justify-between hover:border-black transition-colors">
              <span className="font-light">Pergole</span>
              <ArrowRight strokeWidth={1.2} className="w-4 h-4 text-champagne group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contatti-vetrate-panoramiche" onClick={() => window.scrollTo({ top: 0 })} data-testid="suggestion-preventivo" className="group border border-black/10 p-5 flex items-center justify-between hover:border-black transition-colors bg-black text-white hover:bg-champagne">
              <span className="font-light">Richiedi un Preventivo</span>
              <ArrowRight strokeWidth={1.2} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {related.length > 0 && (
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24 mb-24" data-testid="article-related">
            <h2 className="font-serif font-light text-3xl tracking-tight mb-10 text-center">Articoli correlati</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {related.map((a) => (
                <BlogCard key={a.slug} article={a} />
              ))}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
}
